import { HttpUrlEncodingCodec } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Facet, ProductSearchPage } from '@spartacus/core';
import { BreakpointService, FacetService, ICON_TYPE, ProductListComponentService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { PLPRefinementState } from 'src/app/model/cache-page-state.model';
import { CachePageStateService } from 'src/app/service/cache-page-state/cache-page-state.service';
import { ComponentService } from 'src/app/service/component.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoProductListComponentService } from 'src/app/service/product/shimano-product-list-component.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';

@Component({
  selector: 'app-product-refinement',
  templateUrl: './product-refinement.component.html',
  styleUrls: ['./product-refinement.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductRefinementComponent implements OnInit, OnDestroy, OnChanges {
  @Input() sidenav: string;
  @Input() sidenavopen: any;
  private sub: Subscription;

  iconTypes = ICON_TYPE;

  activeFacetValueCode: string;
  searchResult: ProductSearchPage;
  minPerFacet = 5;
  // tslint:disable-next-line: ban-types
  showAllPerFacetMap: Map<any, boolean>;
  protected queryCodec: HttpUrlEncodingCodec;
  collapsedFacets = new Set<string>();
  searchResult$: Observable<ProductSearchPage>;
  visibleFacets$: Observable<Facet[]>;
  facets: any;
  formatFacet: any;
  isLeafCategory = false;
  showClearFilter = false;
  bottomSpaceForMobileLandscape = false;
  activeQuery: string;
  facetNames: any = [];
  gtmSearchResults: any;
  cxRoute: string;
  plpCachingEnabled: boolean;
  cachedPageData: PLPRefinementState;
  breakpointServiceSubscription$: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private productListComponentService: ProductListComponentService,
    private componentService: ComponentService,
    private facetService: FacetService,
    protected router: Router,
    private gtmService: GoogleTagManagerService,
    private cachePageStateService: CachePageStateService,
    private cdr: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    private shimanoProductListComponentService: ShimanoProductListComponentService
  ) {
    // tslint:disable-next-line: ban-types
    this.showAllPerFacetMap = new Map<any, boolean>();
    this.queryCodec = new HttpUrlEncodingCodec();
  }

  ngOnInit(): void {
    this.sub = this.activatedRoute.params.subscribe((params) => {
      this.activeFacetValueCode = params.categoryCode || params.brandCode;
      this.activeQuery = params.query;
    });

    this.searchResult$ = this.productListComponentService.model$.pipe(
      tap((searchResult) => {
        if (searchResult.facets) {
          const facetGroup: any[] = [];
          this.formatFacet = [];
          this.facets = searchResult.facets;
          this.gtmSearchResults = searchResult;
          this.isLeafCategory = this.checkIsLeafCategory(searchResult.facets[0]) || false;
          searchResult.facets.forEach((el: any) => {
            this.showAllPerFacetMap.set(el.priority, false);
            if (typeof el.groupName !== 'undefined' && facetGroup.indexOf(el.groupName) < 0) {
              const filterGroupName = this.facets;
              this.formatFacet.push({
                name: el.groupName,
                values: filterGroupName.filter((facet: { groupName: any }) => facet.groupName === el.groupName),
              });
              facetGroup.push(el.groupName);
            } else if (typeof el.groupName === 'undefined') {
              this.formatFacet.push(el);
              this.showAllPerFacetMap.set(el.priority, true);
              this.bottomSpaceForMobileLandscape = true;
            }
          });
          if (searchResult.breadcrumbs) {
            this.componentService.setPlPBreadcrumbs(searchResult.breadcrumbs);
            this.showClearFilter = this.checkToShowClearFilter(searchResult.breadcrumbs);
          }
        }
      }),
      map((searchResult, index) => {
        if (this.cachedPageData) {
          if (this.sidenav !== 'true') {
            this.collapsedFacets = this.cachedPageData.collapsedFacets;
          }
          this.shimanoProductListComponentService.collapsedFacets$.next(this.cachedPageData.collapsedFacets);
          this.shimanoProductListComponentService.showMoreFacets$.next(this.cachedPageData.showMoreFacets);
        }

        this.syncShowMoreFacets();

        if (index === 1) {
          // clear cached data on second emit of model$
          this.cacheCleanUp();
        }

        return searchResult;
      })
    );

    this.visibleFacets$ = this.searchResult$.pipe(
      map((searchResult) => {
        return searchResult.facets ? this.formatFacet : [];
      })
    );

    this.breakpointServiceSubscription$ = this.breakpointService.breakpoint$.subscribe((breakpoint) => {
      this.syncShowMoreFacets();
    });

    this.isPLPCachingEnabled();
    this.watchBackBtnBrowserClick();
  }

  toggleValue(query: string, facet: string, queryName: string): void {
    const queryIndex = this.router.url.indexOf('?');
    const getUrlnoParam = queryIndex !== -1 ? this.router.url.substring(0, queryIndex) : this.router.url;
    if (this.sidenav === 'true' && this.collapsedFacets.size > 0) {
      this.hideFacet();
    }

    if (query === '') {
      if (this.sidenav === 'true') {
        this.collapsedFacets.clear();
      }
      this.router.navigateByUrl(getUrlnoParam);
      const breadcrumbs: any[] = [];
      this.componentService.setPlPBreadcrumbs(breadcrumbs);
    } else {
      let queryparam = this.facetService.getLinkParams(this.queryCodec.encodeValue(query));

      this.gtmRefinement(query, facet, queryName);

      if (getUrlnoParam !== '') {
        this.router.navigateByUrl(getUrlnoParam + '?query=' + queryparam?.query);
      } else {
        this.router.navigateByUrl(this.router.url + '?query=' + queryparam?.query);
      }
    }
    this.componentService.fromProductRefinement$.next(true);
  }

  gtmRefinement(query: string, facet: string, queryName: string) {
    let activeFacet = false;
    this.gtmSearchResults.breadcrumbs.forEach((item: any) => {
      if (queryName == item.facetValueName) {
        activeFacet = true;
      }
    });
    if (query && facet && queryName) {
      if (!this.facetNames.includes(facet) && !activeFacet) {
        this.facetNames.push(facet);
        this.gtmService.pushTag({
          ecommerce: null,
          search_term: '',
          results_page: '',
          affiliation: '',
          content_type: '',
          content_id: '',
          content_name: '',
        });

        this.gtmService.pushTag({
          event: GoogleTagManagerEvent.CLICK_FACET,
          ecommerce: null,
          facet_name: facet,
          clicked_item_name: queryName,
          clicked_item_value: query,
        });
      }
    }
  }

  // tslint:disable-next-line: ban-types
  showLess(priority: any): void {
    this.updateShowAllPerFacetMap(priority, false);
    this.setShowMoreFacets();
  }

  // tslint:disable-next-line: ban-types
  showMore(priority: any): void {
    this.updateShowAllPerFacetMap(priority, true);
    this.setShowMoreFacets();
  }

  // tslint:disable-next-line: ban-types
  private updateShowAllPerFacetMap(priority: any, showAll: boolean): void {
    this.showAllPerFacetMap.set(priority, showAll);
  }

  isFacetCollapsed(facetName: string): boolean {
    return this.collapsedFacets?.has(facetName);
  }

  toggleFacet(facetName: string, facetId: number): void {
    if (this.collapsedFacets?.has(facetName)) {
      if (this.sidenav === 'true') {
        this.showFacet();
      }
      this.collapsedFacets.delete(facetName);
    } else {
      if (this.sidenav === 'true') {
        this.collapsedFacets.clear();
        this.hideFacet();
        // show the active facet
        const facetGroup = document.querySelectorAll('.sidenav-facet-group');
        // tslint:disable-next-line: prefer-for-of
        if (this.isLeafCategory) {
          facetId--;
        }
        for (let facet = 0; facet < facetGroup.length; facet++) {
          if (facet === facetId) {
            facetGroup[facet].classList.remove('d-none');
            break;
          }
        }
      } else {
        this.shimanoProductListComponentService.collapsedFacets$.next(this.collapsedFacets);
      }
      this.collapsedFacets.add(facetName);
    }
  }

  hideFacet(): void {
    if (document.querySelector('.side-nav-sorting') !== null) {
      document.querySelector('.side-nav-sorting').classList.add('d-none');
    }
    if (document.querySelector('.sidenav-facet-filter-container') !== null) {
      document.querySelector('.sidenav-facet-filter-container').classList.add('d-none');
    }
    if (document.querySelector('.sidenav-facet-group') !== null) {
      document.querySelector('.sidenav-facet-group').classList.add('d-none');
    }
  }

  showFacet(): void {
    if (document.querySelector('.side-nav-sorting') !== null) {
      document.querySelector('.side-nav-sorting').classList.remove('d-none');
    }
    if (document.querySelector('.sidenav-facet-filter-container') !== null) {
      document.querySelector('.sidenav-facet-filter-container').classList.remove('d-none');
    }
    if (document.querySelector('.sidenav-facet-group') !== null) {
      document.querySelector('.sidenav-facet-group').classList.remove('d-none');
    }
  }

  getVisibleFacetValues(facet: { values: string | any[]; priority: any }): any {
    return facet.values.slice(0, this.showAllPerFacetMap.get(facet.priority) ? facet.values.length : this.minPerFacet);
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(40);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  checkIsLeafCategory(allCategoriesFacet: any): boolean {
    if (typeof allCategoriesFacet !== 'undefined') {
      return allCategoriesFacet.values.length === 1;
    }
  }

  checkToShowClearFilter(breadcrumbs: any[]) {
    const exclude = breadcrumbs.filter((crumbs) => !crumbs.facetCode.includes('category'));
    return exclude.length <= 0 ? false : true;
  }

  setShowMoreFacets(): void {
    // list of facets that are in 'Show More' state
    const showMoreFacets = [...this.showAllPerFacetMap]
      .filter(([priority, value]) => value === true)
      .map(([priority]) => priority);

    this.shimanoProductListComponentService.showMoreFacets$.next(showMoreFacets);
  }

  syncShowMoreFacets(): void {
    const showMoreFacets = this.shimanoProductListComponentService.showMoreFacets$.value;
    if (showMoreFacets) {
      this.showAllPerFacetMap.forEach((value: boolean, priority: string) => {
        if (showMoreFacets.includes(priority)) {
          this.showAllPerFacetMap.set(priority, true);
        }
      });
      this.cdr.detectChanges();
    }
  }

  isPLPCachingEnabled(): void {
    this.cachePageStateService
      .getPLPCachingEnabledConfig$()
      .pipe(take(1))
      .subscribe((enabled) => (this.plpCachingEnabled = enabled));
  }

  enablePLPCaching(): void {
    const page = this.activatedRoute.snapshot.data.cxRoute;

    // added if-condition to only execute only once
    // since there are two instances of this component: with and without sidenav
    if (this.sidenav === 'true') {
      const data: PLPRefinementState = {
        collapsedFacets: this.shimanoProductListComponentService.collapsedFacets$.value,
        showMoreFacets: this.shimanoProductListComponentService.showMoreFacets$.value,
      };

      this.cachePageStateService.enableCacheOnPage(page, data);
      this.shimanoProductListComponentService.collapsedFacets$.next(new Set());
      this.shimanoProductListComponentService.showMoreFacets$.next(null);
    }
  }

  cacheCleanUp() {
    this.cachedPageData = null;
    this.cachePageStateService.clearPage(this.cxRoute);
  }

  watchBackBtnBrowserClick(): void {
    this.activatedRoute.queryParams.subscribe((data) => {
      this.cxRoute = this.activatedRoute.snapshot.data.cxRoute;

      if (this.cachePageStateService.isNavigatedViaBackBtn()) {
        this.cachedPageData = this.cachePageStateService.getCachedPage(this.cxRoute);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.sidenavopen.currentValue) {
      this.collapsedFacets.clear();
      this.showFacet();
    }
  }

  ngOnDestroy() {
    if (this.plpCachingEnabled) {
      this.enablePLPCaching();
    }
    this.sub?.unsubscribe();
    this.breakpointServiceSubscription$?.unsubscribe();
  }
}
